import React, {useState, useEffect, useRef} from "react";
import TextInput from "../../../components/TextInput";
import {useDispatch, useSelector} from "react-redux";
import {ThunkDispatch} from "@reduxjs/toolkit";
import CustomIconButton from "../../../components/CustomIconButton";
import {formatPadStartNumber} from "utils/format";
import InfiniteScroll from "react-infinite-scroll-component";
import { LoadingIcon } from "../../../components/Icons";
import ReturnButton from "../../../components/ReturnButton";
import CheckboxButton from "../../../components/CheckboxButton";
import CustomButton from "../../../components/CustomButton";
import {
    setTalentTagsList, getTalentTagsPanel, updateSelectedTalentTagsPanel, setCurrentPageTagsList
} from "reducers/companyTalent";
import { setIsOpenSearchTagsPanel } from "reducers/talentTag";
import { useNavigate } from "react-router-dom";
import { setIsFormDirty, setOtherPendingPath } from "reducers/app";
import UnsaveConfirmPopup from "../../action/components/UnsaveConfirmPopup";

interface TagItemProps {
    item: any;
}
const TagItem: React.FC<TagItemProps> = ({ item }) => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const [disabledSelected, setDisabledSelected] = useState(false);
    const { 
        talentTagsList, selectedTalent, talentTagsSelected
    } = useSelector((state: any) => state.companyTalent);

    const handleSelectedTalentTags = (tagList) => {
        const selectedTags = tagList.filter((item) => item.selected).map(item => item.id);
        const tagIds = selectedTags.length > 0 ? selectedTags.join(',') : ""
        dispatch(updateSelectedTalentTagsPanel({id: Number(selectedTalent?.talent?.id), tagIds: tagIds}))
    }

    const handleCheckButtonTag = () => {
        const newList = talentTagsList.map((tag) =>
        tag?.id === item?.id
            ? { ...tag, selected: !item?.selected }
            : { ...tag }
        );
        dispatch(setTalentTagsList(newList));

        const newAllTagsSelected = talentTagsSelected.map((tag) =>
        tag?.id === item?.id
            ? { ...tag, selected: !item?.selected }
            : { ...tag }
        );
        handleSelectedTalentTags(newAllTagsSelected);
    }

    return (
        <div onClick={handleCheckButtonTag} className={`p-3 flex items-center gap-3 rounded-[0.25rem] border border-[#E0E0E0] ${talentTagsSelected.filter((tag) => tag.selected).length >= 10 && !item.selected ? 'opacity-50 pointer-events-none' : 'hover:bg-[#FFF3E5] hover:cursor-pointer'}`}>
            <div className="shrink-0">
                <CheckboxButton
                    name={'option_' + item?.id}
                    label={item?.title}
                    isShowingLabel={false}
                    value={item?.selected}
                    onChange={handleCheckButtonTag}
                    disabled={talentTagsSelected.filter((tag) => tag.selected).length >= 10 && !item.selected}
                />
            </div>
            <div className="">
                <div className="w-fit px-2 py-[0.125rem] text-center rounded-sm bg-[#F7F7F7] text-sm alu-text-base-2">
                {item?.title}
                </div>
                {item?.description && <p className="text-xs alu-text-base-1 px-2 mt-[0.375rem] break-all">{item?.description}</p>}
            </div>
        </div>
    );
};

const TalentTagsPanel = ({onClose, setIsTagsPanel}) => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const navigate = useNavigate();
    const [inputValue, setInputValue] = useState("");
    const [isReload, setIsReload] = useState(false);
    const { 
        talentTagsList, hasMore, selectedTalent, currentPageTagsList, pageSizeTagsList
    } = useSelector((state: any) => state.companyTalent);
    const {isFormDirty, otherPendingPath} = useSelector((state: any) => state.app);
    const [showLeaveConfirm1, setShowLeaveConfirm1] = useState(false);
    const scrollableDivRef = useRef<HTMLDivElement>(null);
    const [firstFetchingFinish, setFirstFetchingFinish] = useState(false);

    useEffect(() => {
        fetchTalentTag(1);
        setFirstFetchingFinish(true);
    }, [isReload])

    useEffect(() => {
        const checkAndFetch = () => {
            if (hasMore && scrollableDivRef.current && talentTagsList.length > 0 && firstFetchingFinish) {
                const { scrollHeight, clientHeight } = scrollableDivRef.current;

                if (scrollHeight <= clientHeight) {
                    fetchData();
                }
            }
        };

        checkAndFetch();
        
        window.addEventListener("resize", checkAndFetch);

        return () => {
            window.removeEventListener("resize", checkAndFetch);
        };
    }, [talentTagsList.length, hasMore, firstFetchingFinish]);

    const fetchTalentTag = (page=currentPageTagsList) => {
        const data = {
            page: page,
            pageSize: pageSizeTagsList,
            searchQuery: inputValue,
        }
        dispatch(getTalentTagsPanel({id: Number(selectedTalent?.talent?.id), ...data}));
        dispatch(setCurrentPageTagsList(page));
    }

    const handleSetSearchQuery = (value: any) => {
        setInputValue(value);
        dispatch(setCurrentPageTagsList(1));
        setIsReload(!isReload);
    };

    const fetchData = () => {
        setTimeout(() => {
            const page = parseInt(currentPageTagsList) + 1;
            dispatch(setCurrentPageTagsList(page));
            fetchTalentTag(page)
        }, 800);
    }

    const goToTagsList = () => {
        if(isFormDirty && !showLeaveConfirm1) {
            setShowLeaveConfirm1(true);
            dispatch(setOtherPendingPath("/company/talent/tags"));
            return;
        }
        dispatch(setIsOpenSearchTagsPanel(true));
        navigate('/company/talent/tags');
    }

    const handleConfirmDiscardUnsentMessage = () => {
        dispatch(setIsFormDirty(false));
        setShowLeaveConfirm1(false);
        dispatch(setIsOpenSearchTagsPanel(true));
        navigate(otherPendingPath);
    }

    const handleClose = () => {
        dispatch(setIsOpenSearchTagsPanel(false))
        onClose()
    }

    return (
        <>
            <div className="flex items-center justify-between px-4 mb-4">
                <ReturnButton onClick={() => setIsTagsPanel(false)} />
                <CustomIconButton onClick={handleClose}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <g clipPath="url(#clip0_8574_265)">
                        <path
                            d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                            fill="currentColor"
                        />
                        </g>
                        <defs>
                        <clipPath id="clip0_8574_265">
                            <rect width="24" height="24" fill="white" />
                        </clipPath>
                        </defs>
                    </svg>
                </CustomIconButton>
            </div>
            <div className="mb-4 px-4">
                <div className="flex items-center gap-3 mb-6">
                    <div className="flex-1">
                        <TextInput
                            id="search"
                            name="search"
                            value={inputValue}
                            onChange={(e: any) => handleSetSearchQuery(e.target.value)}
                            placeholder="検索"
                            searchIcon={true}
                            onClearQuery={() => handleSetSearchQuery("")}
                        />
                    </div>
                    <CustomButton variant="text" customStyle="!shrink-0" onClick={() => goToTagsList()}>
                        タグ管理
                    </CustomButton>
                </div>
                <p className="text-sm alu-text-base-1">最大10個まで選択できます</p>
            </div>
            <div id="scrollableDiv" ref={scrollableDivRef} className="flex-1 px-4 pb-4 flex flex-col gap-1 overflow-y-auto custom-scrollbar">
                <InfiniteScroll
                    dataLength={talentTagsList.length}
                    next={fetchData}
                    hasMore={hasMore}
                    loader={<div className="flex justify-center p-2"><LoadingIcon /></div>}
                    scrollableTarget="scrollableDiv"
                    scrollThreshold={0.5}
                >
                    <div className="flex flex-col gap-[0.375rem]">
                        {talentTagsList?.length > 0 &&
                        talentTagsList.map((tag: any, idx: any) => <TagItem key={idx} item={tag} />)}
                    </div>
                </InfiniteScroll>
            </div>
            {showLeaveConfirm1 && <UnsaveConfirmPopup
                open={showLeaveConfirm1}
                onClose={() => setShowLeaveConfirm1(false)}
                confirmContent={"編集中の内容を保存せずに終了しますか？"}
                onOtherAction={handleConfirmDiscardUnsentMessage}
            />}
        </>
    );
};

export default TalentTagsPanel;
